/**
 * Audio: Mistral-generated MP3s when available (preferred for clarity), browser
 * TTS as fallback. Web Audio for SFX. Question prompts are guarded so only
 * the latest requested voice can play, avoiding delayed double reads.
 */
import { resolveLocalAudioUrl } from "../data/local-assets";

const PLAYBACK_RATE = 0.85; // slow + natural for ESL learners
const MUSIC_URL = "/audio/music/Ribcage%20Rumba.mp3";
const SFX_URLS = {
  menu: "/audio/sfx/mixkit-change-menu-option-small-volume.mp3",
  finish: "/audio/sfx/mixkit-finish-race.mp3",
  correct: "/audio/sfx/mixkit-get-question-right.mp3",
  levelUp: "/audio/sfx/mixkit-level-up.mp3",
} as const;

let ctx: AudioContext | null = null;
let audioVolume = readAudioVolume();
let menuMusic: HTMLAudioElement | null = null;
let musicWanted = false;
let menuFadeId = 0;

function readAudioVolume(): number {
  try {
    const raw = localStorage.getItem("wr_game_settings_v1");
    if (!raw) return 1;
    const parsed = JSON.parse(raw) as { audioVolume?: number };
    const n = Number(parsed.audioVolume);
    return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 1;
  } catch {
    return 1;
  }
}

function readMusicVolume(): number {
  try {
    const raw = localStorage.getItem("wr_game_settings_v1");
    if (!raw) return 0.5;
    const parsed = JSON.parse(raw) as { musicVolume?: number };
    const n = Number(parsed.musicVolume);
    return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0.5;
  } catch {
    return 0.5;
  }
}

function ensureMenuMusic(): HTMLAudioElement {
  if (!menuMusic) {
    menuMusic = new Audio(MUSIC_URL);
    menuMusic.loop = true;
    menuMusic.preload = "auto";
  }
  menuMusic.volume = readMusicVolume();
  return menuMusic;
}

export function refreshAudioSettings() {
  audioVolume = readAudioVolume();
  if (menuMusic) menuMusic.volume = readMusicVolume();
}

export function startMenuMusic() {
  musicWanted = true;
  menuFadeId++;
  const el = ensureMenuMusic();
  if (el.volume <= 0) return;
  el.play().catch(() => {
    const unlock = () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      if (musicWanted) void ensureMenuMusic().play().catch(() => {});
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
  });
}

export function stopMenuMusic() {
  musicWanted = false;
  menuFadeId++;
  if (menuMusic) menuMusic.pause();
}

export function fadeOutMenuMusic(durationMs = 1200) {
  musicWanted = false;
  if (!menuMusic || menuMusic.paused) return;
  const el = menuMusic;
  const fadeId = ++menuFadeId;
  const startVolume = el.volume;
  const startedAt = performance.now();
  const step = () => {
    if (fadeId !== menuFadeId) return;
    const t = Math.min(1, (performance.now() - startedAt) / durationMs);
    el.volume = startVolume * (1 - easeOutCubic(t));
    if (t < 1) {
      requestAnimationFrame(step);
      return;
    }
    el.pause();
    el.currentTime = 0;
    el.volume = readMusicVolume();
  };
  requestAnimationFrame(step);
}

function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3);
}

function audio(): AudioContext {
  if (!ctx) ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

function tone(
  freq: number,
  durMs: number,
  type: OscillatorType = "sine",
  gain = 0.15,
  delayMs = 0,
) {
  const a = audio();
  const o = a.createOscillator();
  const g = a.createGain();
  const start = a.currentTime + delayMs / 1000;
  const end = start + durMs / 1000;
  o.type = type;
  o.frequency.setValueAtTime(freq, start);
  g.gain.setValueAtTime(gain * audioVolume, start);
  g.gain.exponentialRampToValueAtTime(0.0001, end);
  o.connect(g).connect(a.destination);
  o.start(start);
  o.stop(end);
}

function playClip(url: string, volumeScale = 1): boolean {
  try {
    const el = new Audio(url);
    el.volume = Math.max(0, Math.min(1, audioVolume * volumeScale));
    void el.play().catch(() => {});
    return true;
  } catch {
    return false;
  }
}

export const sfx = {
  coin: () => tone(880, 90, "sine", 0.2),
  hit: () => tone(140, 250, "sawtooth", 0.25),
  correct: () => {
    if (playClip(SFX_URLS.correct, 0.54)) return;
    tone(660, 100);
    setTimeout(() => tone(880, 140), 90);
  },
  wrong: () => {
    tone(220, 180, "square", 0.2);
    setTimeout(() => tone(180, 220, "square", 0.2), 160);
  },
  finish: () => {
    if (playClip(SFX_URLS.finish, 0.9)) return;
    tone(523, 130);
    setTimeout(() => tone(659, 130), 120);
    setTimeout(() => tone(784, 200), 240);
  },
  menu: () => {
    if (playClip(SFX_URLS.menu, 0.65)) return;
    tone(660, 55, "sine", 0.08);
  },
  levelUp: () => {
    if (playClip(SFX_URLS.levelUp, 0.85)) return;
    tone(523, 100);
    setTimeout(() => tone(784, 130), 100);
    setTimeout(() => tone(1046, 180), 220);
  },
  countdown: (label: string) => {
    if (label === "GO!") {
      tone(880, 360, "square", 0.22);
      tone(1760, 260, "sine", 0.08);
      tone(660, 90, "triangle", 0.12, 70);
      return;
    }
    tone(220, 170, "square", 0.2);
    tone(110, 170, "sawtooth", 0.06);
  },
};

let currentAudio: HTMLAudioElement | null = null;
let voiceSerial = 0;
let voiceUnlockQueued = false;

function stopCurrent() {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.src = "";
    currentAudio = null;
  }
  if ("speechSynthesis" in window) window.speechSynthesis.cancel();
}

function speakWithBrowser(text: string, lang: string) {
  if (!("speechSynthesis" in window)) return;
  const u = new SpeechSynthesisUtterance(text);
  u.lang = lang;
  u.rate = 0.95;
  u.volume = audioVolume;
  window.speechSynthesis.speak(u);
}

function queueVoiceUnlockRetry(fn: () => void) {
  if (voiceUnlockQueued) return;
  voiceUnlockQueued = true;
  const unlock = () => {
    voiceUnlockQueued = false;
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
    fn();
  };
  window.addEventListener("pointerdown", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });
}

function playVoiceAudio(
  text: string,
  audioUrl: string,
  lang: string,
  serial: number,
  allowGestureRetry: boolean,
  allowBrowserFallback: boolean,
) {
  const el = new Audio(audioUrl);
  let fellBack = false;
  const fallbackOnce = () => {
    if (fellBack || serial !== voiceSerial) return;
    fellBack = true;
    if (currentAudio === el) currentAudio = null;
    if (allowBrowserFallback) speakWithBrowser(text, lang);
  };
  el.preload = "auto";
  el.playbackRate = PLAYBACK_RATE;
  el.preservesPitch = true;
  el.volume = audioVolume;
  currentAudio = el;
  el.onerror = fallbackOnce;
  el.onended = () => {
    if (currentAudio === el) currentAudio = null;
  };
  el.play().catch(() => {
    if (serial !== voiceSerial) return;
    if (allowGestureRetry) {
      queueVoiceUnlockRetry(() => {
        if (serial === voiceSerial) playVoiceAudio(text, audioUrl, lang, serial, false, allowBrowserFallback);
      });
      return;
    }
    fallbackOnce();
  });
}

/**
 * Speak `text`. If `audioUrl` is provided, plays that MP3 (slowed via
 * playbackRate). Otherwise falls back to browser TTS.
 */
export function speak(text: string, opts: { audioUrl?: string | null; lang?: string } = {}) {
  const { audioUrl, lang = "en-US" } = opts;
  const serial = ++voiceSerial;
  speakForSerial(text, audioUrl, lang, serial);
}

function speakForSerial(text: string, audioUrl: string | null | undefined, lang: string, serial: number) {
  stopCurrent();
  const localAudioUrl = resolveLocalAudioUrl(audioUrl);
  if (localAudioUrl) {
    playVoiceAudio(text, localAudioUrl, lang, serial, true, false);
    return;
  }
  speakWithBrowser(text, lang);
}

export interface LoopHandle {
  cancel: () => void;
}

export function speakPromptOnce(
  text: string,
  opts: { audioUrl?: string | null; lang?: string } = {},
): LoopHandle {
  const loopSerial = ++voiceSerial;
  let cancelled = false;
  let timer = 0;
  const tick = () => {
    if (cancelled || loopSerial !== voiceSerial) return;
    speakForSerial(text, opts.audioUrl, opts.lang ?? "en-US", loopSerial);
    timer = window.setTimeout(tick, 4300);
  };
  // Defer first tick ~120ms so React StrictMode double-mount doesn't kill it.
  timer = window.setTimeout(tick, 120);
  return {
    cancel: () => {
      cancelled = true;
      window.clearTimeout(timer);
      stopCurrent();
    },
  };
}
